import "./add-edit-template-modal.css";

import { DialogActionsBar } from "@tql/ui-components-react/kendo/dialogs";
import { Field, Form } from "@tql/ui-components-react/kendo/form";
import { Checkbox } from "@tql/ui-components-react/kendo/inputs";
import { Button } from "@tql/ui-components-react/tql/buttons";
import { useAtom } from "jotai";
import { useCallback, useContext, useEffect, useRef, useState } from "react";

import { useAddressBookItems } from "~/features/address-book/hooks/use-address-book.ts";
import { initVal } from "~/features/quote/components/quote-details/constants/quote-detail-constants.ts";
import { QuoteDetailsForm } from "~/features/quote/components/quote-details/index.tsx";
import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import {
	QuoteDetailsContext,
	QuoteDetailsProvider,
} from "~/features/quote/context/quote-details-context.tsx";
import { useAddressBookAccessorials } from "~/features/quote/hooks/use-address-book-accessorials.ts";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import { isPristineSavedAddress } from "~/features/quote/utils/applied-address.ts";
import { formatQuoteStateForTemplate } from "~/features/quote/utils/format-quote-state.ts";
import { mapQuoteBodyToQuoteState } from "~/features/quote/utils/map-quote-body-to-quote-state.ts";
import type { LocationValue } from "~/features/quote/utils/quote-location-utils.ts";
import { verifyStopAddressBookId } from "~/features/quote/utils/quote-location-utils.ts";
import {
	useCreateTemplate,
	useUpdateTemplate,
} from "~/features/templates/hooks/use-create-template.ts";
import {
	mapTemplateToGridItem,
	useTemplateDefault,
} from "~/features/templates/hooks/use-templates.ts";
import { InputField } from "~/shared/components/fields/input-field.tsx";
import { TraxModal } from "~/shared/components/trax-modal/trax-modal.tsx";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import type { QuoteBody } from "~/shared/schemas/quote-schema.ts";
import type {
	TemplateGridItem,
	TemplateResponse,
} from "~/shared/schemas/template-schema.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";
import type { QuoteStateType } from "~/shared/types/quote-details-types.ts";
import { base64Decode, base64Encode } from "~/shared/utils/base64-utils.ts";

type AddEditTemplateModalProps = {
	toggleModal: () => void;
	template?: TemplateGridItem;
	onApplyTemplate?: (template: TemplateGridItem) => void;
	autoApplyOnSave?: boolean;
	initialQuoteState?: QuoteStateType;
	initialSelectedAddressBookItems?: SelectedAddressBookItems;
};

type TemplateFormValues = {
	templateName: string;
};

export const AddEditTemplateModal = ({
	toggleModal,
	template,
	onApplyTemplate,
	autoApplyOnSave,
	initialQuoteState,
	initialSelectedAddressBookItems,
}: AddEditTemplateModalProps) => {
	return (
		<QuoteDetailsProvider initialQuoteState={initialQuoteState}>
			<AddNewTemplateModalContent
				autoApplyOnSave={autoApplyOnSave}
				initialQuoteState={initialQuoteState}
				initialSelectedAddressBookItems={initialSelectedAddressBookItems}
				onApplyTemplate={onApplyTemplate}
				template={template}
				toggleModal={toggleModal}
			/>
		</QuoteDetailsProvider>
	);
};

const AddNewTemplateModalContent = ({
	toggleModal,
	template,
	onApplyTemplate,
	autoApplyOnSave,
	initialQuoteState,
	initialSelectedAddressBookItems,
}: AddEditTemplateModalProps) => {
	const isEditMode = !!template;
	const [templateName, setTemplateName] = useState(
		template?.templateName ?? "",
	);
	const [isDefault, setIsDefault] = useState(template?.isDefault ?? false);
	const [error, setError] = useState<string | null>(null);
	const [selectedCustomer] = useAtom(selectedCustomerAtom);
	const { quoteState, setQuoteState, setCustomerDetails, resetForm } =
		useContext(QuoteDetailsContext);
	const {
		selectedAddressBookItems,
		setSelectedAddressBookItem,
		resetSelectedAddressBookItem,
	} = useQuoteAddressContext();
	const { data: pageData } = useQuoteDetailsPageLoadQueries();
	const { allAccessorials } = useAddressBookAccessorials();
	// Customer's current saved addresses, used to re-verify a template's stored
	// addressBookItemId during edit-mode hydration so stale links (old templates /
	// changed addresses) do not re-show an edited stop as a pristine saved address.
	const { isInternal } = useDeploymentType();
	const addressBookCustomerId = selectedCustomer?.id ?? 0;
	const {
		data: addressBook,
		isLoading: isAddressBookLoading,
		isError: isAddressBookError,
	} = useAddressBookItems(
		{ customerId: addressBookCustomerId },
		addressBookCustomerId > 0,
		isInternal,
	);
	const { mutateAsync: createTemplate, isPending: isCreating } =
		useCreateTemplate();
	const { mutateAsync: updateTemplate, isPending: isUpdating } =
		useUpdateTemplate();
	const { toggleDefault, isHandlingDefault, userConfigs } = useTemplateDefault(
		selectedCustomer?.id,
	);
	const didInitFromTemplate = useRef(false);
	const didInitFromInitialQuote = useRef(false);
	const didInitDefault = useRef(false);

	// The quoteState is already initialized via QuoteDetailsProvider's
	// initialQuoteState prop, so we only need to handle address book items here.
	useEffect(() => {
		if (isEditMode) {
			return;
		}

		if (!initialQuoteState) {
			return;
		}

		if (didInitFromInitialQuote.current) {
			return;
		}

		if (initialSelectedAddressBookItems?.pickup) {
			setSelectedAddressBookItem(
				"pickup",
				initialSelectedAddressBookItems.pickup,
			);
		} else {
			resetSelectedAddressBookItem("pickup");
		}

		if (initialSelectedAddressBookItems?.delivery) {
			setSelectedAddressBookItem(
				"delivery",
				initialSelectedAddressBookItems.delivery,
			);
		} else {
			resetSelectedAddressBookItem("delivery");
		}

		didInitFromInitialQuote.current = true;
	}, [
		initialQuoteState,
		initialSelectedAddressBookItems,
		isEditMode,
		resetSelectedAddressBookItem,
		setSelectedAddressBookItem,
	]);

	useEffect(() => {
		if (!isEditMode || !template) {
			return;
		}

		resetForm();
		didInitFromTemplate.current = false;
		didInitFromInitialQuote.current = false;
		didInitDefault.current = false;
	}, [isEditMode, template, resetForm]);

	useEffect(() => {
		if (!isEditMode || !template || !selectedCustomer) {
			return;
		}

		if (didInitDefault.current) {
			return;
		}

		const isTemplateDefault = userConfigs.some(
			(config) =>
				config.configType === ConfigurationType.QuoteTemplateDefault &&
				config.customerId === selectedCustomer.id &&
				config.configValue === String(template.templateId),
		);

		setIsDefault(isTemplateDefault);
		didInitDefault.current = true;
	}, [isEditMode, template, selectedCustomer, userConfigs]);

	const isPending = !!(isCreating || isUpdating || isHandlingDefault);

	useEffect(() => {
		if (selectedCustomer) {
			setCustomerDetails(selectedCustomer);
		}
	}, [selectedCustomer, setCustomerDetails]);

	type SubmitResult = { success: true } | { success: false; error: string };

	const buildErrorResult = useCallback(
		(message: string): SubmitResult => {
			setError(message);
			return { success: false, error: message };
		},
		[setError],
	);

	const validateTemplateInputs = useCallback(
		(currentTemplateName: string): string | undefined => {
			if (!currentTemplateName.trim()) {
				return "Template name is required";
			}

			if (!selectedCustomer) {
				return "Customer is required";
			}

			return undefined;
		},
		[selectedCustomer],
	);

	const templateNameValidator = useCallback((value: string | undefined) => {
		return value?.trim() ? "" : "Template name is required";
	}, []);

	type AddressBookMetadata = {
		addressBookItemId: number;
		isLegacy: boolean;
	};

	const buildAddressBookMetadata = useCallback(() => {
		const pickup = selectedAddressBookItems.pickup;
		const delivery = selectedAddressBookItems.delivery;
		const pickupMetadata: AddressBookMetadata | null = isPristineSavedAddress(
			pickup,
		)
			? {
					addressBookItemId: pickup.addressBookItemId,
					isLegacy: pickup.isLegacy,
				}
			: null;
		const deliveryMetadata: AddressBookMetadata | null = isPristineSavedAddress(
			delivery,
		)
			? {
					addressBookItemId: delivery.addressBookItemId,
					isLegacy: delivery.isLegacy,
				}
			: null;

		return { pickupMetadata, deliveryMetadata };
	}, [selectedAddressBookItems]);

	const parseTemplateBody = useCallback(() => {
		const body = formatQuoteStateForTemplate(
			quoteState,
			selectedAddressBookItems,
		);
		const withCalculatedLinearFeet = {
			...body,
			calculatedLinearFeet: quoteState.totalLinearFeet,
			// These are shipment-specific fields that appear only on the
			// /ltl-shipment page (Shipment Value, Product, SO/PO Numbers, Special
			// Instructions) — they are not part of the Create Quote Template modal,
			// so they must never be persisted to a template. Only fields visible in
			// the modal (customer, addresses, commodities, accessorials, customer
			// references) are saved.
			insuranceValue: null,
			insuranceApplyType: null,
			insuranceApplyTypeId: null,
			insuranceProductType: null,
			insuranceProductTypeId: null,
			soNumbers: "",
			poNumbers: "",
			poNumber: null,
			specialInstructions: "",
		};
		const { pickupMetadata, deliveryMetadata } = buildAddressBookMetadata();
		const withAddressBookMetadata = {
			...withCalculatedLinearFeet,
			pickupDetails:
				withCalculatedLinearFeet.pickupDetails && pickupMetadata
					? { ...withCalculatedLinearFeet.pickupDetails, ...pickupMetadata }
					: withCalculatedLinearFeet.pickupDetails,
			deliveryDetails:
				withCalculatedLinearFeet.deliveryDetails && deliveryMetadata
					? { ...withCalculatedLinearFeet.deliveryDetails, ...deliveryMetadata }
					: withCalculatedLinearFeet.deliveryDetails,
		};

		return { success: true, data: withAddressBookMetadata } as const;
	}, [buildAddressBookMetadata, quoteState, selectedAddressBookItems]);

	const buildTemplateToApply = useCallback(
		(args: {
			templateId: number;
			templateName: string;
			templateJson: string;
			rawTemplate?: TemplateResponse;
		}): TemplateGridItem => {
			const now = new Date().toISOString();
			const fallbackRaw: TemplateResponse = {
				templateId: args.templateId,
				name: args.templateName,
				typeEnum: "Quote",
				customerId: selectedCustomer?.id ?? 0,
				userId: 0,
				templateJson: args.templateJson,
				templateSchemaVersion: null,
				createdByUserId: 0,
				createdDate: now,
				lastModifiedByUserId: 0,
				lastModifiedDate: now,
			};

			const raw = args.rawTemplate ?? fallbackRaw;
			const mappedTemplate = mapTemplateToGridItem(
				raw,
				pageData?.freightClassData,
			);

			return {
				...mappedTemplate,
				isFavorite: template?.isFavorite,
				isDefault,
			};
		},
		[isDefault, pageData?.freightClassData, selectedCustomer?.id, template],
	);

	const handleSuccessfulTemplateSave = useCallback(
		async (args: {
			templateId?: number;
			templateName: string;
			templateJson: string;
			rawTemplate?: TemplateResponse;
			applyAfterSave?: boolean;
		}): Promise<SubmitResult> => {
			if (args.templateId) {
				await toggleDefault(args.templateId, isDefault);
			}

			if (!args.applyAfterSave || !onApplyTemplate) {
				toggleModal();
				return { success: true };
			}

			if (!args.templateId) {
				return buildErrorResult("Failed to apply template");
			}

			onApplyTemplate(
				buildTemplateToApply({
					templateId: args.templateId,
					templateName: args.templateName,
					templateJson: args.templateJson,
					rawTemplate: args.rawTemplate,
				}),
			);

			return { success: true };
		},
		[
			toggleDefault,
			isDefault,
			onApplyTemplate,
			toggleModal,
			buildErrorResult,
			buildTemplateToApply,
		],
	);

	// Initialize quote state from template when in edit mode
	useEffect(() => {
		if (!isEditMode || !template) {
			return;
		}

		if (!selectedCustomer) {
			return;
		}

		if (didInitFromTemplate.current) {
			return;
		}

		if (!pageData || !allAccessorials || allAccessorials.length === 0) {
			return;
		}

		// Wait for the customer's saved addresses before hydrating so the stored
		// template addressBookItemId can be re-verified. Gate on the query's
		// loading state (not `addressBook === undefined`) so an address-book fetch
		// failure — which also leaves `data` undefined — does not block hydration
		// forever. On error we still hydrate, but detach stored ids below so a
		// stale link is never trusted as a pristine saved address.
		if (addressBookCustomerId > 0 && isAddressBookLoading) {
			return;
		}

		try {
			const data = JSON.parse(
				base64Decode(template.raw.templateJson),
			) as Partial<QuoteBody>;

			const hydratedInitialState = {
				...initVal,
				customer: {
					...initVal.customer,
					...selectedCustomer,
				},
				customerDetails: {
					...initVal.customerDetails,
					...selectedCustomer,
				},
			};
			const { nextState, pickupItem, deliveryItem } = mapQuoteBodyToQuoteState(
				data,
				hydratedInitialState,
				{
					accessorials: allAccessorials,
					freightClasses: pageData.freightClassData,
					unitTypes: pageData.unitTypes,
				},
			);

			// Trust the template's stored addressBookItemId only when the stop still
			// exactly matches a current saved address; otherwise detach it (id 0) so
			// a stale link does not re-show an edited stop as a saved address. If the
			// address book failed to load, pass an empty list so no stored id is
			// trusted (verifyStopAddressBookId would otherwise no-op on a nullish
			// address book and keep the stale id).
			const verificationAddressBook = isAddressBookError ? [] : addressBook;
			const verifiedPickup = verifyStopAddressBookId(
				pickupItem,
				data.pickupDetails,
				data.origin as LocationValue | undefined,
				"pickup",
				verificationAddressBook,
			);
			const verifiedDelivery = verifyStopAddressBookId(
				deliveryItem,
				data.deliveryDetails,
				data.destination as LocationValue | undefined,
				"delivery",
				verificationAddressBook,
			);

			if (verifiedPickup) {
				setSelectedAddressBookItem("pickup", verifiedPickup);
			}
			if (verifiedDelivery) {
				setSelectedAddressBookItem("delivery", verifiedDelivery);
			}

			didInitFromTemplate.current = true;
			setQuoteState(nextState);
		} catch (error) {
			console.error("Failed to parse template JSON:", error);
			setError("Failed to load template data");
		}
	}, [
		isEditMode,
		template,
		setQuoteState,
		setSelectedAddressBookItem,
		allAccessorials,
		pageData,
		selectedCustomer,
		addressBook,
		addressBookCustomerId,
		isAddressBookLoading,
		isAddressBookError,
	]);

	const handleTemplateSubmit = useCallback(
		async (
			values?: TemplateFormValues,
			options?: { applyAfterSave?: boolean },
		) => {
			const resolvedTemplateName = (
				values?.templateName ?? templateName
			).trim();
			setError(null);
			const validationError = validateTemplateInputs(resolvedTemplateName);
			if (validationError) {
				return buildErrorResult(validationError);
			}

			if (!selectedCustomer) {
				return buildErrorResult("Customer is required");
			}

			const parsedTemplateBody = parseTemplateBody();

			const templateData = {
				name: resolvedTemplateName,
				typeEnum: "Quote",
				customerId: selectedCustomer.id,
				userId: 0,
				templateJson: base64Encode(JSON.stringify(parsedTemplateBody.data)),
			};

			const result =
				isEditMode && template
					? await updateTemplate({
							templateId: template.templateId,
							body: templateData,
						})
					: await createTemplate(templateData);

			if (result.success) {
				const templateId = isEditMode
					? template?.templateId
					: result.data?.templateId;
				const rawTemplate =
					result.data ??
					(isEditMode && template
						? {
								...template.raw,
								templateId: templateId ?? template.templateId,
								name: resolvedTemplateName,
								templateJson: templateData.templateJson,
							}
						: undefined);

				return handleSuccessfulTemplateSave({
					templateId,
					templateName: resolvedTemplateName,
					templateJson: templateData.templateJson,
					rawTemplate,
					applyAfterSave: options?.applyAfterSave,
				});
			}

			const errorMessage = result.error ?? "Failed to save template";
			return buildErrorResult(errorMessage);
		},
		[
			buildErrorResult,
			parseTemplateBody,
			validateTemplateInputs,
			templateName,
			isEditMode,
			template,
			selectedCustomer,
			createTemplate,
			updateTemplate,
			handleSuccessfulTemplateSave,
		],
	);

	const handleSaveAndApply = useCallback(() => {
		void handleTemplateSubmit(undefined, { applyAfterSave: true });
	}, [handleTemplateSubmit]);

	const handleSave = useCallback(() => {
		void handleTemplateSubmit(undefined, {
			applyAfterSave: autoApplyOnSave,
		});
	}, [autoApplyOnSave, handleTemplateSubmit]);

	return (
		<TraxModal
			className={"add-new-template-modal"}
			height={"75%"}
			onClose={toggleModal}
			title={isEditMode ? "Edit Quote Template" : "Create Quote Template"}
			width={"80%"}
		>
			<Form
				initialValues={{ templateName }}
				onSubmit={(values) => {
					void handleTemplateSubmit(values as TemplateFormValues, {
						applyAfterSave: autoApplyOnSave,
					});
				}}
				render={() => (
					<>
						<div className={"add-new-template-header"}>
							<div className={"template-name-input"}>
								<Field
									component={InputField}
									disabled={isPending}
									hint={false}
									hintDirection={undefined}
									id={"templateName"}
									label={
										<>
											{isEditMode ? "Template Name" : "New Quote Template Name"}
											<span className={"required"}> *</span>
										</>
									}
									name={"templateName"}
									onChange={(event: { value: string }) => {
										setTemplateName(event.value);
										setError(null);
									}}
									required={true}
									validator={templateNameValidator}
									wrapperStyle={{ minWidth: 300 }}
								/>
							</div>
							<div className={"template-default-checkbox"}>
								<Checkbox
									checked={isDefault}
									id={"isDefaultTemplate"}
									label={"Set as Default Template"}
									onChange={(e) => setIsDefault(e.value)}
								/>
							</div>
						</div>
						<QuoteDetailsForm
							isSubmitting={isPending}
							onStepChange={() => {}}
							onSubmit={handleTemplateSubmit}
							submitButtonText={
								isEditMode ? "Update Template" : "Save Template"
							}
							templateMode={true}
						/>
					</>
				)}
			/>
			<DialogActionsBar layout={"end"}>
				{error ? <div className={"template-modal-error"}>{error}</div> : null}
				{onApplyTemplate ? (
					<Button
						disabled={isPending}
						onClick={handleSaveAndApply}
						variant={"primary"}
					>
						{isEditMode ? "Update" : "Save"} and Apply Template
					</Button>
				) : null}
				<Button disabled={isPending} onClick={handleSave} variant={"primary"}>
					{isEditMode ? "Update Template" : "Save Template"}
				</Button>
				<Button onClick={toggleModal} variant={"tertiary"}>
					Cancel
				</Button>
			</DialogActionsBar>
		</TraxModal>
	);
};
